import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getVerifiedUsers,
  assignUserRoles,
  resetVerifiedUsersState,
} from "../../store/superAdmin/roleSlice";
import { fetchFilteredUsersByEmailAndRole } from "../../store/common/searchSlice";
import { fetchPartnerTypes } from "../../store/admin/partnersSlice";
import { User } from "../../types/User";
import { useToast } from "../../components/utilities/Toast";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminMetaRow,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const roles: User["role"][] = [
  "Super Admin",
  "Side Admin",
  "Admin Read",
  "Admin",
  "Operations",
  "Production",
  "User",
];

const roleStyles: Record<User["role"], React.CSSProperties> = {
  "Super Admin": { backgroundColor: "#1f2937", color: "white" },
  "Side Admin": { backgroundColor: "#4b5563", color: "white" },
  "Admin Read": { backgroundColor: "#9ca3af", color: "black" },
  Admin: { backgroundColor: "#000000", color: "white" },
  Operations: { backgroundColor: "#ff5c8a", color: "white" },
  Production: { backgroundColor: "#ffba08", color: "black" },
  User: { backgroundColor: "#e5e7eb", color: "black" },
};

const AccessToggle = ({
  enabled,
  disabled = false,
  edited = false,
  onChange,
}: {
  enabled: boolean;
  disabled?: boolean;
  edited?: boolean;
  onChange: (next: boolean) => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!enabled)}
    className={`inline-flex items-center gap-2 select-none ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    }`}
    title={enabled ? "Click to disable" : "Click to enable"}
  >
    <span
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
        enabled ? "bg-emerald-500" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
          enabled ? "translate-x-[22px]" : "translate-x-[2px]"
        }`}
      />
    </span>
    <span
      className={`text-xs font-semibold ${
        enabled ? "text-emerald-600" : "text-gray-500"
      }`}
    >
      {enabled ? "Enabled" : "Disabled"}
      {edited ? " *" : ""}
    </span>
  </button>
);

const entriesPerPage = 50;

const Roles = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { verifiedUsers, isLoading } = useAppSelector(
    (state) => state.adminRoleControls
  );

  const { partners } = useAppSelector((state) => state.adminPartner);

  const filteredUsersState = useAppSelector(
    (state) => state.search.filteredUsers
  );

  const users = Array.isArray(verifiedUsers)
    ? verifiedUsers
    : verifiedUsers?.users || [];
  const totalPages = verifiedUsers?.totalPages || 1;

  const filteredUsers = Array.isArray(filteredUsersState)
    ? filteredUsersState
    : filteredUsersState && "data" in filteredUsersState
      ? (filteredUsersState as { data: User[] }).data
      : filteredUsersState && "users" in filteredUsersState
        ? (filteredUsersState as { users: User[] }).users
        : [];

  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"email" | "token">("token");
  const [selectedRole, setSelectedRole] = useState<User["role"] | "">("");
  const [portalAccess, setPortalAccess] = useState<"" | "smile" | "cases">("");
  const [page, setPage] = useState(1);
  const [editedRoles, setEditedRoles] = useState<Record<number, User["role"]>>(
    {}
  );
  const [editedPartner, setEditedPartner] = useState<Record<number, string>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [partnerName, setPartnerName] = useState("");

  const usersToDisplay =
    Array.isArray(filteredUsers) && (searchTerm || selectedRole)
      ? filteredUsers
      : Array.isArray(users)
        ? users
        : [];

  const isSearching = !!(searchTerm || selectedRole);

  useEffect(() => {
    if (searchTerm || selectedRole) {
      dispatch(
        fetchFilteredUsersByEmailAndRole({
          ...(searchType === "token" ? { token: searchTerm } : { email: searchTerm }),
          role: selectedRole || undefined,
          portalAccess: portalAccess || undefined,
        })
      );
    } else {
      dispatch(
        getVerifiedUsers({
          page,
          limit: entriesPerPage,
          search: "",
          portalAccess,
        })
      );
      dispatch(fetchPartnerTypes());
    }

    return () => {
      dispatch(resetVerifiedUsersState());
    };
  }, [dispatch, page, searchTerm, selectedRole, searchType, portalAccess]);
  const handleRoleEdit = (userId: number, newRole: User["role"]) => {
    setEditedRoles((prev) => {
      if (
        newRole === "Side Admin" &&
        Object.values(prev).includes("Side Admin") &&
        prev[userId] !== "Side Admin"
      ) {
        alert("Only one user can be assigned as Side Admin at a time.");
        return prev;
      }
      return { ...prev, [userId]: newRole };
    });
  };

  const handlePartnerEdit = (userId: number, partner: string) => {
    setEditedPartner((prev) => ({
      ...prev,
      [userId]: partner,
    }));
  };

  const handleApplyChanges = async () => {
    const userIds = new Set([
      ...Object.keys(editedRoles),
      ...Object.keys(editedPartner),
      ...Object.keys(editedSmile),
      ...Object.keys(editedCases),
      ...Object.keys(editedRecolour),
      ...Object.keys(editedSurgery),
    ]);

    const updateData = Array.from(userIds).map((id) => {
      const role = editedRoles[Number(id)];

      return {
        user_id: Number(id),
        role,
        ...(role === "Side Admin" ? { partner: partnerName } : {}),
        updatedPartner: editedPartner[Number(id)] || "",
        verifySmile: editedSmile[Number(id)],
        verified: editedCases[Number(id)],
        verifyPhotoRecolour: editedRecolour[Number(id)],
        verifySurgeryBooking: editedSurgery[Number(id)],
        photoRecolourCredits:
          editedRecolour[Number(id)] === true
            ? editedRecolourCredits[Number(id)] ?? 0
            : undefined,
      };
    });

    const sideAdminCount = updateData.filter(
      (u) => u.role === "Side Admin"
    ).length;

    if (sideAdminCount > 1) {
      alert("You can only assign one user as Side Admin at a time.");
      return;
    }

    if (updateData.length > 0) {
      await dispatch(assignUserRoles({ updateData }));
      setEditedRoles({});
      setEditedPartner({});
      setEditedSmile({});
      setEditedCases({});
      setEditedRecolour({});
      setEditedRecolourCredits({});
      setEditedSurgery({});
      showToast(`Roles updated for ${updateData.length} user(s)`, "success");
    }
  };

  const sideAdminUserId = Object.entries(editedRoles).find(
    ([, role]) => role === "Side Admin"
  )?.[0];

  const [editedSmile, setEditedSmile] = useState<Record<number, boolean>>({});
  const [editedCases, setEditedCases] = useState<Record<number, boolean>>({});
  const [editedRecolour, setEditedRecolour] = useState<Record<number, boolean>>({});
  const [editedSurgery, setEditedSurgery] = useState<Record<number, boolean>>({});
  const [editedRecolourCredits, setEditedRecolourCredits] = useState<
    Record<number, number>
  >({});
  const [recolourCreditPrompt, setRecolourCreditPrompt] = useState<{
    userId: number;
    email: string;
    name: string;
  } | null>(null);
  const [recolourCreditDraft, setRecolourCreditDraft] = useState("0");

  const handleSmileEdit = (userId: number, verifySmile: boolean) => {
    setEditedSmile((prev) => ({
      ...prev,
      [userId]: verifySmile,
    }));
  };

  const handleCasesEdit = (userId: number, verified: boolean) => {
    setEditedCases((prev) => ({
      ...prev,
      [userId]: verified,
    }));
  };

  const handleSurgeryEdit = (userId: number, verifySurgeryBooking: boolean) => {
    setEditedSurgery((prev) => ({
      ...prev,
      [userId]: verifySurgeryBooking,
    }));
  };

  const handleRecolourEdit = (userId: number, verifyPhotoRecolour: boolean) => {
    if (verifyPhotoRecolour) {
      const user = usersToDisplay.find((u) => u.user_id === userId);
      setRecolourCreditPrompt({
        userId,
        email: user?.email || "",
        name: user?.user_name || "",
      });
      setRecolourCreditDraft("0");
      return;
    }

    setEditedRecolour((prev) => ({
      ...prev,
      [userId]: false,
    }));
    setEditedRecolourCredits((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const confirmRecolourCredits = () => {
    if (!recolourCreditPrompt) return;
    const credits = Math.max(
      0,
      Math.floor(Number(recolourCreditDraft) || 0),
    );
    setEditedRecolour((prev) => ({
      ...prev,
      [recolourCreditPrompt.userId]: true,
    }));
    setEditedRecolourCredits((prev) => ({
      ...prev,
      [recolourCreditPrompt.userId]: credits,
    }));
    setRecolourCreditPrompt(null);
    setRecolourCreditDraft("0");
  };

  const hasPendingChanges =
    Object.keys(editedRoles).length > 0 ||
    Object.keys(editedPartner).length > 0 ||
    Object.keys(editedSmile).length > 0 ||
    Object.keys(editedCases).length > 0 ||
    Object.keys(editedRecolour).length > 0 ||
    Object.keys(editedSurgery).length > 0;

  const colCount = 10;

  return (
    <>
      <Helmet>
        <title>Assign Roles</title>
      </Helmet>

      <AdminPageShell>
        {/* Header */}
        <AdminPageHeader
          title="Assign Roles"
          subtitle="Manage user roles, partners, and portal access"
        >
          <AdminSearchInput
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            placeholder={
              searchType === "email" ? "Search by Email…" : "Search by Token…"
            }
            className="w-full sm:w-[220px]"
          />

          <AdminSelect
            value={searchType}
            onChange={(value) => {
              setSearchType(value as "token" | "email");
              setSearchTerm("");
            }}
            className="w-full sm:w-auto"
          >
            <option value="token">Token</option>
            <option value="email">Email</option>
          </AdminSelect>

          <AdminSelect
            value={selectedRole}
            onChange={(value) => {
              setSelectedRole(value as User["role"] | "");
              setPage(1);
            }}
            className="w-full sm:w-auto"
          >
            <option value="">All Roles</option>
            {roles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </AdminSelect>

          <AdminSelect
            value={portalAccess}
            onChange={(value) => {
              setPortalAccess(value as "" | "smile" | "cases");
              setPage(1);
            }}
            className="w-full sm:w-auto"
          >
            <option value="">All Portals</option>
            <option value="smile">Smile Only</option>
            <option value="cases">Cases Only</option>
          </AdminSelect>
        </AdminPageHeader>

        {/* Meta row */}
        <AdminMetaRow
          left={
            <span>
              {usersToDisplay.length} user
              {usersToDisplay.length === 1 ? "" : "s"}
              {isSearching ? " matched" : " on this page"}
            </span>
          }
          right={
            <>
              {!isSearching ? (
                <AdminPagination
                  page={page}
                  totalPages={totalPages}
                  onPrev={() => setPage((p) => p - 1)}
                  onNext={() => setPage((p) => p + 1)}
                  disabled={isLoading}
                />
              ) : null}
              <AdminPrimaryButton
                onClick={() => setShowConfirmModal(true)}
                disabled={!hasPendingChanges}
              >
                Apply Changes
              </AdminPrimaryButton>
            </>
          }
        />

        {/* Table */}
        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={adminTableHead}>User ID</th>
                <th className={adminTableHead}>Token</th>
                <th className={adminTableHead}>Name</th>
                <th className={adminTableHead}>Email</th>
                <th className={adminTableHead}>Role</th>
                <th className={adminTableHead}>Partner</th>
                <th className={adminTableHead}>Cases Access</th>
                <th className={adminTableHead}>Smile Access</th>
                <th className={adminTableHead}>Recolour Access</th>
                <th className={adminTableHead}>Surgery Access</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading users…"
                />
              ) : !Array.isArray(usersToDisplay) ||
                usersToDisplay.length === 0 ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message="No users found"
                />
              ) : (
                usersToDisplay.map((item) => {
                  const currentRole = editedRoles[item.user_id] ?? item.role;
                  const currentPartner =
                    editedPartner[item.user_id] ?? (item.partner === "" ? "admin" : item.partner);

                  const roleEdited = editedRoles[item.user_id] !== undefined;
                  const partnerEdited = editedPartner[item.user_id] !== undefined;
                  const smileEdited = editedSmile[item.user_id] !== undefined;
                  const casesEdited = editedCases[item.user_id] !== undefined;
                  const recolourEdited = editedRecolour[item.user_id] !== undefined;
                  const surgeryEdited = editedSurgery[item.user_id] !== undefined;
                  const accessEdited =
                    smileEdited || casesEdited || recolourEdited || surgeryEdited;

                  return (
                    <tr
                      key={item.user_id}
                      className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors"
                    >
                      <td className={adminTableCell}>
                        {String(item.user_id).padStart(5, "0")}
                      </td>
                      <td className={adminTableCell}>{item.token}</td>
                      <td className={adminTableCell}>{item.user_name}</td>
                      <td className={adminTableCell}>{item.email}</td>
                      <td className={adminTableCell}>
                        {item.role === "Super Admin" ? (
                          <span
                            style={{
                              ...roleStyles["Super Admin"],
                              borderRadius: "8px",
                              padding: "4px 10px",
                              fontWeight: "bold",
                              display: "inline-block",
                            }}
                            className="text-xs"
                          >
                            Super Admin
                          </span>
                        ) : (
                          <select
                            className="cursor-pointer rounded-lg text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={partnerEdited || accessEdited}
                            value={currentRole}
                            onChange={(e) =>
                              handleRoleEdit(
                                item.user_id,
                                e.target.value as User["role"]
                              )
                            }
                            style={{
                              ...roleStyles[currentRole],
                              borderRadius: "8px",
                              padding: "4px 10px",
                              fontWeight: "bold",
                            }}
                          >
                            {roles
                              .filter((role) => role !== "Super Admin")
                              .map((role) => (
                                <option
                                  key={role}
                                  value={role}
                                  style={roleStyles[role]}
                                >
                                  {role}
                                </option>
                              ))}
                          </select>
                        )}
                      </td>
                      <td className={adminTableCell}>
                        <select
                          className={`cursor-pointer rounded-lg border px-2.5 py-1.5 text-sm ${
                            roleEdited || accessEdited
                              ? "opacity-50 cursor-not-allowed bg-[#F8F8F6] border-[#E8E8E4]"
                              : "bg-[#F8F8F6] border-[#DDDDD8] text-[#4A4A48] focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 outline-none"
                          }`}
                          disabled={roleEdited || smileEdited || casesEdited || recolourEdited || surgeryEdited}
                          value={currentPartner || ""}
                          onChange={(e) =>
                            handlePartnerEdit(item.user_id, e.target.value)
                          }
                        >
                          <option value="admin">Admin</option>
                          {partners?.map((p: string) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={adminTableCell}>
                        <AccessToggle
                          enabled={
                            editedCases[item.user_id] !== undefined
                              ? editedCases[item.user_id]
                              : !!item.verified
                          }
                          edited={casesEdited}
                          disabled={roleEdited || partnerEdited || smileEdited || recolourEdited || surgeryEdited}
                          onChange={(next) =>
                            handleCasesEdit(item.user_id, next)
                          }
                        />
                      </td>
                      <td className={adminTableCell}>
                        <AccessToggle
                          enabled={
                            editedSmile[item.user_id] !== undefined
                              ? editedSmile[item.user_id]
                              : !!item.verifySmile
                          }
                          edited={smileEdited}
                          disabled={roleEdited || partnerEdited || casesEdited || recolourEdited || surgeryEdited}
                          onChange={(next) =>
                            handleSmileEdit(item.user_id, next)
                          }
                        />
                      </td>
                      <td className={adminTableCell}>
                        <div className="flex flex-col gap-1">
                          <AccessToggle
                            enabled={
                              editedRecolour[item.user_id] !== undefined
                                ? editedRecolour[item.user_id]
                                : !!item.verifyPhotoRecolour
                            }
                            edited={recolourEdited}
                            disabled={roleEdited || partnerEdited || casesEdited || smileEdited || surgeryEdited}
                            onChange={(next) =>
                              handleRecolourEdit(item.user_id, next)
                            }
                          />
                          {editedRecolour[item.user_id] === true ? (
                            <span className="text-[11px] text-[#8A8A85]">
                              {editedRecolourCredits[item.user_id] ?? 0} free
                              credit
                              {(editedRecolourCredits[item.user_id] ?? 0) === 1
                                ? ""
                                : "s"}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className={adminTableCell}>
                        <AccessToggle
                          enabled={
                            editedSurgery[item.user_id] !== undefined
                              ? editedSurgery[item.user_id]
                              : !!item.verifySurgeryBooking
                          }
                          edited={surgeryEdited}
                          disabled={roleEdited || partnerEdited || casesEdited || smileEdited || recolourEdited}
                          onChange={(next) =>
                            handleSurgeryEdit(item.user_id, next)
                          }
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </AdminTableCard>

        {/* Confirm Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border border-[#E8E8E4]">
              <h2 className="text-base font-semibold text-[#4A4A48]">
                Confirm Role Assignment
              </h2>
              <p className="text-sm text-[#8A8A85] leading-relaxed">
                Are you sure you want to apply changes for{" "}
                <strong className="text-[#4A4A48]">
                  {
                    new Set([
                      ...Object.keys(editedRoles),
                      ...Object.keys(editedPartner),
                      ...Object.keys(editedSmile),
                      ...Object.keys(editedCases),
                      ...Object.keys(editedRecolour),
                      ...Object.keys(editedSurgery),
                    ]).size
                  }
                </strong>{" "}
                user(s)?
              </p>

              {sideAdminUserId && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-1.5">
                    Partner Name (required for Side Admin)
                  </label>
                  <input
                    type="text"
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20"
                    placeholder="Enter partner name"
                    required
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (sideAdminUserId && !partnerName.trim()) {
                      alert("Please enter a partner name for the Side Admin.");
                      return;
                    }
                    await handleApplyChanges();
                    setShowConfirmModal(false);
                  }}
                  className="px-4 py-2 rounded-xl bg-[#2fa9bf] hover:bg-[#2296aa] text-white text-xs font-medium transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {recolourCreditPrompt && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border border-[#E8E8E4]">
              <h2 className="text-base font-semibold text-[#4A4A48]">
                Photo Recolour free credits
              </h2>
              <p className="text-sm text-[#8A8A85] leading-relaxed">
                How many free Photo Recolour credits do you want to give{" "}
                <strong className="text-[#4A4A48]">
                  {recolourCreditPrompt.name || recolourCreditPrompt.email || "this client"}
                </strong>
                {recolourCreditPrompt.email ? (
                  <>
                    {" "}
                    <span className="text-[#8A8A85]">
                      ({recolourCreditPrompt.email})
                    </span>
                  </>
                ) : null}
                ? Leave <strong className="text-[#4A4A48]">0</strong> if they
                should buy credits before generating.
              </p>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-1.5">
                  Free credits
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={recolourCreditDraft}
                  onChange={(e) => setRecolourCreditDraft(e.target.value)}
                  className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setRecolourCreditPrompt(null);
                    setRecolourCreditDraft("0");
                  }}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmRecolourCredits}
                  className="px-4 py-2 rounded-xl bg-[#2fa9bf] hover:bg-[#2296aa] text-white text-xs font-medium transition"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminPageShell>
    </>
  );
};

export default Roles;
